/**
 * New request — one sheet for a contact, a property, a job and a site visit
 * (`docs/new-request.md`, Boards 2–9, 13–16).
 *
 * This file owns the state and the rules of the sheet and nothing about how
 * a section looks: which sections are open, waiting or folded (§5.0), what a
 * choice in the search fills in (§5.4), when the calendars are beside the form
 * (§10.1), what Create is missing (§11.3), and the one call that creates it
 * all (§11.2). The four sections and the schedule aside read it through
 * `RequestSheetProvider`.
 *
 * - **Desktop and iPad landscape**: the record flyout at 620, growing to 1,180
 *   with the calendars to its left (`Sheet`'s `aside`).
 * - **iPad portrait**: opening a calendar replaces the form (§16.2).
 * - **Phone**: the full screen, *Cancel · New request · Create* along the top
 *   and no footer; a calendar is a screen of its own (§16.3).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { uuidv7 } from 'uuidv7'
import {
  BUDGET_BANDS, PREFERS_LABELS, clock, dayShort, formatPhone, segmentOf,
  type BookRequestInput, type ConfirmInput, type DayPlan, type KnownContact, type KnownProperty, type NewRequestInput,
  type NewRequestResult,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { Sheet, SHEET_ASIDE_MIN_WINDOW } from '../components/sheet.js'
import { Button } from '../components/primitives.js'
import { CheckGlyph, Keycap, PlusGlyph } from '../components/keycap.js'
import { IconCalendar } from '../frame/icons.js'
import { requestsApi, type BookContextView, type RequestSetup } from './api.js'
import {
  EMPTY_SHEET, EMPTY_TYPED_CONTACT, SECTIONS, appointmentStarted, anythingTyped, channelFor,
  bookNote, contactFirstName, contactName, contactStatus, footerNote, missingFor, missingWords, subjectName,
  propertyName as sheetPropertyName, propertyStatus, readyCount, statusOf,
  type Missing, type SectionKey, type SectionStatus, type SheetState,
} from './model.js'
import {
  RequestSheetProvider, useRecommendation, useRequestSheet, type BookContext, type ChosenTime, type PersonRef,
  type RequestFacts, type RequestSheetApi, type ScheduleState,
} from './sheet-context.js'
import { FoldedLine, SectionBar, WaitingRow } from './section-frame.js'
import { SearchBox, type SearchChoice } from './search-box.js'
import { BottomMenu } from './bottom-menu.js'
import { SheetRow } from './create-rows.js'
import { ContactSection, contactSummary, knownContactLine } from './contact-section.js'
import { PropertySection, propertySummary } from './property-section.js'
import { JobSection, jobSummaryOf } from './job-section.js'
import { AppointmentSection, appointmentSummaryOf } from './appointment-section.js'
import { wantedToSend } from './when-field.js'
import { NarrowSchedule, PhoneSchedule, ScheduleAside } from './schedule/index.js'

export interface NewRequestPrefill {
  contactId?: string
  propertyId?: string
  /** Book mode (§11.4): a job that exists, and the appointment to give a time to. */
  book?: { jobId: string; appointmentId?: string | null }
  /** Typed into the search to start from — an unknown number from the Inbox (Activity, Board 8 B). */
  search?: string
}

/** What the page does after Create: the toast's words, and where to go. */
export interface RequestOutcome {
  /** Null when the request made no job — a contact, or a contact and a place. */
  jobId: string | null
  reference: string | null
  /** The job (at the not-booked visit for *Create, book later*), else the place, else the contact. */
  landing: string | null
  toast: { title: string; sub: string; openJob: boolean }
}

export interface NewRequestSheetProps {
  open: boolean
  prefill: NewRequestPrefill | null
  onClose: () => void
  onDone: (outcome: RequestOutcome) => void
}

const firstWord = (name: string | null | undefined): string => (name ?? '').trim().split(/\s+/)[0] ?? ''

/** *9:41*, the person's own clock (Board 2). */
function startedAt(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone })
      .format(new Date(iso)).replace(/\s?[AP]M$/i, '')
  } catch {
    return ''
  }
}

function usePortraitTablet(): boolean {
  const phone = usePhone()
  const query = `(max-width: ${SHEET_ASIDE_MIN_WINDOW - 1}px)`
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const list = window.matchMedia(query)
    const change = () => setNarrow(list.matches)
    list.addEventListener('change', change)
    return () => list.removeEventListener('change', change)
  }, [query])
  return narrow && !phone
}

// ---------------------------------------------------------------------------
// The sheet
// ---------------------------------------------------------------------------

/** What the confirmation asks for: an email's extra addresses and its own opening go with it. */
function confirmOf(state: SheetState, channel: 'sms' | 'email'): ConfirmInput {
  const appointment = state.appointment
  return channel === 'email'
    ? { channel, alsoTo: appointment.confirmAlsoTo, message: appointment.confirmMessage?.trim() || null }
    : { channel, alsoTo: [] }
}

/** The boards' footer buttons: 36 px, 13 px semibold, 14 px either side, a 6 px gap to the glyph. */
const FOOTER_BUTTON = 'h-9 gap-1.5 px-3.5 text-[13px]'

export function NewRequestSheet({ open, prefill, onClose, onDone }: NewRequestSheetProps) {
  const phone = usePhone()
  const narrow = usePortraitTablet()
  const [requestId, setRequestId] = useState(() => uuidv7())
  const [setup, setSetup] = useState<RequestSetup | null>(null)
  const [state, setState] = useState<SheetState>(EMPTY_SHEET)
  const [openSections, setOpenSections] = useState<SectionKey[]>(['contact'])
  const [current, setCurrent] = useState<SectionKey | null>('contact')
  const [pressed, setPressed] = useState<{ bookLater: boolean } | null>(null)
  const [schedule, setScheduleState] = useState<ScheduleState | null>(null)
  const [plans, setPlans] = useState<Record<string, DayPlan>>({})
  const [bookAnyway, setBookAnyway] = useState<string | null>(null)
  const [focus, setFocus] = useState<{ field: string; n: number } | null>(null)
  const [replaceAsk, setReplaceAsk] = useState<
    { section: 'contact' | 'property'; name: string; apply: () => void } | null>(null)
  const [book, setBook] = useState<BookContext | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [askNoTime, setAskNoTime] = useState(false)
  const body = useRef<HTMLDivElement>(null)
  const searchInput = useRef<HTMLInputElement>(null)
  /** A focus the sheet moved itself, which must not fold the section the person was in. */
  const quietFocus = useRef(false)
  const stateRef = useRef(state)
  stateRef.current = state

  // -------------------------------------------------------------------------
  // Opening and closing
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!open) return
    const id = uuidv7()
    setRequestId(id)
    setState(EMPTY_SHEET)
    setOpenSections(['contact'])
    setCurrent('contact')
    setPressed(null)
    setScheduleState(null)
    setPlans({})
    setBookAnyway(null)
    setReplaceAsk(null)
    setBook(null)
    setProblem(null)
    setAskNoTime(false)
    setSetup(null)
    requestsApi.setup()
      .then((answer) => {
        setSetup(answer)
        /* The switch starts where the company set it (§12.2, Board 11). */
        setState((was) => ({ ...was, appointment: { ...was.appointment, confirm: answer.confirmDefault } }))
      })
      .catch(() => setProblem('New request could not load. Try again.'))
    return () => { requestsApi.endSession(id).catch(() => {}) }
  }, [open])

  // -------------------------------------------------------------------------
  // Opening and folding sections (§5.0)
  // -------------------------------------------------------------------------

  const order = (list: SectionKey[]): SectionKey[] => SECTIONS.filter((one) => list.includes(one))

  const scrollTo = useCallback((section: SectionKey) => {
    window.requestAnimationFrame(() => {
      const node = document.getElementById(`request-${section}`)
      const scroller = body.current?.closest('.overflow-y-auto') as HTMLElement | null
      const bar = body.current?.querySelector<HTMLElement>('[data-request-sticky]')
      if (!node || !scroller) return
      const top = node.getBoundingClientRect().top - scroller.getBoundingClientRect().top
        + scroller.scrollTop - (bar?.offsetHeight ?? 0) - 12
      scroller.scrollTo?.({ top: Math.max(0, top), behavior: 'smooth' })
    })
  }, [])

  /**
   * The person moved into a section: it opens, and every other section that
   * holds something folds (ready → its card or line, started → the amber
   * line). An empty open section stays as it is (Board 2 B).
   */
  const currentRef = useRef(current)
  currentRef.current = current
  const enter = useCallback((section: SectionKey, options?: { scroll?: boolean }) => {
    const moved = currentRef.current !== section
    setCurrent(section)
    /* A phone keeps the section in hand at the top, under the search (Boards 14 C, 15 A). */
    if (phone && moved && !options?.scroll) scrollTo(section)
    if (quietFocus.current) return
    setOpenSections((list) => order([
      ...list.filter((one) => one === section || statusOf(stateRef.current, one) === 'empty'),
      section,
    ]))
    if (options?.scroll) scrollTo(section)
  }, [scrollTo, phone])

  const openSection = useCallback((section: SectionKey) => enter(section, { scroll: true }), [enter])

  /*
   * Opening the calendars folds what is ready to one line (Board 7), once, as
   * they open — so a section opened again afterwards with *Open* stays open.
   */
  const calendarsOpen = schedule !== null
  useEffect(() => {
    if (!calendarsOpen) return
    setOpenSections((list) => list.filter((one) => one === 'appointment'
      || statusOf(stateRef.current, one) !== 'ready'))
  }, [calendarsOpen])

  /** The flow: the next waiting section after this one opens by itself. */
  const advanceFrom = useCallback((section: SectionKey) => {
    const after = SECTIONS.slice(SECTIONS.indexOf(section) + 1)
    setOpenSections((list) => {
      const next = after.find((one) => !list.includes(one) && statusOf(stateRef.current, one) === 'empty')
      return next ? order([...list, next]) : list
    })
  }, [])

  /* A typed section that becomes ready opens the next (§5.0's standard flow). */
  const statuses = useMemo(() => Object.fromEntries(SECTIONS.map((one) =>
    [one, statusOf(state, one)])) as Record<SectionKey, SectionStatus>, [state])
  const previous = useRef(statuses)
  useEffect(() => {
    const before = previous.current
    previous.current = statuses
    if (book) return
    if (before.contact !== 'ready' && statuses.contact === 'ready' && state.contact.kind === 'new') {
      advanceFrom('contact')
    }
    if (before.property !== 'ready' && statuses.property === 'ready' && state.property.kind === 'new') {
      advanceFrom('property')
    }
    if (before.job !== 'ready' && statuses.job === 'ready') advanceFrom('job')
  }, [statuses, state.contact.kind, state.property.kind, book, advanceFrom])

  const requestFocus = useCallback((field: string) => {
    setFocus((was) => ({ field, n: (was?.n ?? 0) + 1 }))
    window.setTimeout(() => {
      const node = document.querySelector<HTMLElement>(`[data-request-field="${field}"]`)
      if (!node) return
      quietFocus.current = true
      node.focus()
      quietFocus.current = false
    }, 40)
  }, [])

  // -------------------------------------------------------------------------
  // Choosing what is already here (§5.4)
  // -------------------------------------------------------------------------

  /** Typed values are not overwritten without asking (§19 B2). */
  const askOrApply = useCallback((section: 'contact' | 'property', name: string, apply: () => void) => {
    const now = stateRef.current
    const typed = section === 'contact'
      ? now.contact.kind === 'new' && contactStatus(now) !== 'empty' && !now.contactFromProperty
      : now.property.kind === 'new' && propertyStatus(now) !== 'empty'
    if (typed) setReplaceAsk({ section, name, apply })
    else apply()
  }, [])

  const loadProperty = useCallback(async (id: string): Promise<KnownProperty | null> => {
    try { return await requestsApi.property(id) } catch { return null }
  }, [])

  const chooseProperty = useCallback(async (
    propertyId: string, options?: { contactId?: string; fromContact?: boolean; inSection?: boolean },
  ) => {
    const place = await loadProperty(propertyId)
    if (!place) return
    const run = options?.inSection || options?.fromContact
      ? (_section: 'property', _name: string, apply: () => void) => apply() : askOrApply
    run('property', place.name, () => {
      setState((was) => {
        let next: SheetState = { ...was, property: { kind: 'chosen', property: place },
          propertyFromContact: Boolean(options?.fromContact) }
        /*
         * A property offers its people (§5.4): unless a contact is already
         * given — chosen, typed, or named under the property in the search —
         * Contact becomes *who at <property>?* with the primary chosen.
         */
        const typedContact = was.contact.kind === 'new' && contactStatus(was) !== 'empty'
        if (!options?.fromContact && !typedContact
          && (was.contact.kind === 'none' || was.contactFromProperty)) {
          const primary = place.contacts.find((one) => one.isPrimary) ?? place.contacts[0]
          const wanted = options?.contactId
            ? place.contacts.find((one) => one.id === options.contactId) ?? primary : primary
          next = wanted
            ? { ...next, contactFromProperty: true,
              contact: { kind: 'chosen', contact: {
                id: wanted.id, firstName: wanted.firstName, lastName: wanted.lastName,
                phone: wanted.phone, phoneKind: wanted.phoneKind, mobile: wanted.mobile,
                email: wanted.email, prefers: wanted.prefers, optedOut: wanted.optedOut,
                smsOk: wanted.smsOk, company: null, hasJobs: place.jobs > 0, properties: [],
              } } }
            : { ...next, contactFromProperty: place.contacts.length > 0 }
        }
        return next
      })
      setOpenSections((list) => order([...list.filter((one) => one !== 'job'), 'contact', 'property']))
      advanceFrom('property')
    })
  }, [askOrApply, loadProperty, advanceFrom])

  const chooseContact = useCallback(async (
    contactId: string, options?: { propertyId?: string; fromProperty?: boolean },
  ) => {
    let known: KnownContact
    try { known = await requestsApi.contact(contactId) } catch { return }
    const name = `${known.firstName} ${known.lastName}`
    askOrApply('contact', name, async () => {
      setState((was) => ({
        ...was,
        contact: { kind: 'chosen', contact: known },
        contactFromProperty: Boolean(options?.fromProperty)
          || (was.contactFromProperty && was.property.kind === 'chosen'),
      }))
      if (options?.fromProperty) return
      /* A person offers their places: one is chosen; several, the most recently worked (§5.4). */
      const wanted = options?.propertyId
        ?? (stateRef.current.property.kind === 'none' ? known.properties[0]?.id : undefined)
      if (wanted) {
        const place = await loadProperty(wanted)
        if (place) {
          setState((was) => ({ ...was, property: { kind: 'chosen', property: place },
            propertyFromContact: known.properties.length > 1 }))
          if (options?.propertyId) {
            setOpenSections((list) => order([...list, 'contact', 'property']))
            advanceFrom('property')
            return
          }
        }
      } else if (stateRef.current.property.kind === 'none') {
        setState((was) => ({ ...was, propertyFromContact: known.properties.length > 0 }))
      }
      setOpenSections((list) => order([...list, 'contact']))
      advanceFrom('contact')
    })
  }, [askOrApply, loadProperty, advanceFrom])

  const clearSection = useCallback((section: 'contact' | 'property') => {
    setState((was) => section === 'contact'
      ? { ...was, contact: { kind: 'none' },
        contactFromProperty: was.property.kind === 'chosen' && was.property.property.contacts.length > 0 }
      : { ...was, property: { kind: 'none' }, propertyFromContact:
        was.contact.kind === 'chosen' && was.contact.contact.properties.length > 0 })
    if (section === 'property') setScheduleState(null)
    setOpenSections((list) => order([...list, section]))
    setCurrent(section)
    window.setTimeout(() => searchInput.current?.focus(), 30)
  }, [])

  const onSearchChoice = useCallback((choice: SearchChoice) => {
    /*
     * A choice leaves the search (grey again, Boards 3 and 5): a place moves on
     * to the job's title, a person to their places.
     */
    if (choice.kind === 'contact') {
      searchInput.current?.blur()
      setCurrent(choice.propertyId ? 'job' : 'property')
      void chooseContact(choice.contactId, choice.propertyId ? { propertyId: choice.propertyId } : undefined)
        .then(() => { if (choice.propertyId) requestFocus('title') })
    } else if (choice.kind === 'property') {
      searchInput.current?.blur()
      setCurrent('job')
      void chooseProperty(choice.propertyId, choice.contactId ? { contactId: choice.contactId } : undefined)
        .then(() => requestFocus('title'))
    } else if (choice.kind === 'new-contact') {
      const parsed = choice.parsed
      askOrApply('contact', [parsed.firstName, parsed.lastName].filter(Boolean).join(' ') || 'that', () => {
        setState((was) => ({
          ...was,
          contactFromProperty: false,
          contact: { kind: 'new', fields: {
            ...EMPTY_TYPED_CONTACT,
            firstName: parsed.firstName ?? '', lastName: parsed.lastName ?? '',
            phone: parsed.phone ?? '', email: parsed.email ?? '',
          } },
        }))
        setOpenSections((list) => order([...list, 'contact']))
        setCurrent('contact')
        /* The next field to fill: *fills those three fields and moves to Email* (Board 4 B). */
        requestFocus(!parsed.firstName ? 'firstName' : !parsed.lastName ? 'lastName'
          : !parsed.email ? 'email' : !parsed.phone ? 'phone' : 'lastName')
      })
    } else {
      setState((was) => ({
        ...was,
        propertyFromContact: false,
        property: was.property.kind === 'new'
          ? { kind: 'new', property: { ...was.property.property,
            address: { ...was.property.property.address, line1: choice.text } } }
          : { kind: 'new', property: {
            address: {
              line1: choice.text, line2: '', city: '', state: '', postalCode: '', latitude: null,
              longitude: null, placeId: null, verified: false, pinKept: false, needsUnit: false,
            },
            type: 'Residential', name: '', nameIsAuto: true, zoneId: null, orgUnitId: null,
            check: null, anotherUnit: false,
          } },
      }))
      setOpenSections((list) => order([...list, 'property']))
      setCurrent('property')
      requestFocus('address')
    }
  }, [chooseContact, chooseProperty, askOrApply, requestFocus])

  // -------------------------------------------------------------------------
  // Prefill: a record's ⋯ (§13.3) and book mode (§11.4)
  // -------------------------------------------------------------------------

  const prefilled = useRef<string | null>(null)
  useEffect(() => {
    if (!open || !setup || !prefill) return
    if (prefilled.current === requestId) return
    prefilled.current = requestId
    if (prefill.book) {
      void loadBook(prefill.book.jobId, prefill.book.appointmentId ?? null)
      return
    }
    if (prefill.propertyId) void chooseProperty(prefill.propertyId)
    else if (prefill.contactId) void chooseContact(prefill.contactId)
    /* Once per sheet: the request id is what says "this sheet". */
  }, [open, setup, prefill, requestId])

  async function loadBook(jobId: string, appointmentId: string | null) {
    let view: BookContextView
    try { view = await requestsApi.bookContext(jobId) } catch {
      setProblem('That job could not be opened here.')
      return
    }
    const [place, known] = await Promise.all([
      loadProperty(view.propertyId),
      view.contactId ? requestsApi.contact(view.contactId).catch(() => null) : Promise.resolve(null),
    ])
    const appointment = view.appointment && (!appointmentId || view.appointment.id === appointmentId)
      ? view.appointment : null
    setBook({
      jobId: view.jobId, reference: view.reference, appointmentId: appointment?.id ?? null,
      propertyName: place?.name ?? '',
    })
    setState((was) => ({
      ...was,
      contact: known ? { kind: 'chosen', contact: known } : was.contact,
      property: place ? { kind: 'chosen', property: place } : was.property,
      job: { ...was.job, title: view.title, divisionCode: view.divisionCode },
      appointment: {
        ...was.appointment,
        description: appointment?.description ?? '',
        minutes: appointment?.minutes ?? null,
        salespersonId: appointment?.salespersonId ?? null,
        salespersonName: appointment?.salespersonName ?? null,
      },
    }))
    setOpenSections(['appointment'])
    setCurrent('appointment')
    /* Rescheduling is choosing a new time: *When* has the focus (`activity.md` §6.5). */
    requestFocus('time')
    /*
     * §11.4: book mode is the Appointment beside the schedule. With the person
     * already chosen there are no best-time cards to press, so their calendar
     * opens with the sheet wherever the aside fits.
     */
    if (appointment?.salespersonId && appointment.salespersonName && !phone && !narrow) {
      const name = appointment.salespersonName
      setScheduleState({ mode: 'one', day: null, first: null,
        people: [{ id: appointment.salespersonId, name, firstName: name.split(' ')[0] ?? name }] })
    }
  }

  // -------------------------------------------------------------------------
  // The facts every part reads
  // -------------------------------------------------------------------------

  const facts: RequestFacts = useMemo(() => {
    const division = setup?.divisions.find((one) => one.code === state.job.divisionCode) ?? null
    let place: RequestFacts['place'] = null
    let orgUnitId: string | null = null
    let branchName: string | null = null
    let segment: RequestFacts['segment'] = 'residential'
    let street: string | null = null
    if (state.property.kind === 'chosen') {
      const known = state.property.property
      place = known.latitude !== null && known.longitude !== null
        ? { lat: known.latitude, lng: known.longitude } : null
      orgUnitId = known.orgUnitId
      branchName = known.branchName
      segment = known.segment
      street = known.line1
    } else if (state.property.kind === 'new') {
      const typed = state.property.property
      const address = typed.address
      place = address.latitude !== null && address.longitude !== null
        && (address.verified || address.pinKept)
        ? { lat: address.latitude, lng: address.longitude } : null
      orgUnitId = typed.orgUnitId ?? typed.check?.branch?.id ?? null
      branchName = typed.check?.branches.find((one) => one.id === orgUnitId)?.name
        ?? typed.check?.branch?.name ?? null
      segment = segmentOf(typed.type)
      street = address.line1.trim() || null
    }
    return {
      contactId: state.contact.kind === 'chosen' ? state.contact.contact.id : null,
      contactFirstName: contactFirstName(state),
      contactName: contactName(state),
      place,
      orgUnitId,
      branchName,
      divisionCode: division?.code ?? state.job.divisionCode,
      divisionName: division?.name ?? null,
      divisionMinutes: division?.estimateMinutes ?? null,
      segment,
      minutes: state.appointment.minutes ?? division?.estimateMinutes ?? 60,
      propertyName: sheetPropertyName(state),
      street,
      jobTitle: state.job.title.trim() || null,
    }
  }, [state, setup])

  const { recommendation, loading: recommendationLoading } = useRecommendation(requestId, facts)

  /* A new place is a new set of drives: the remembered days were measured from the old one. */
  const placeKey = facts.place ? `${facts.place.lat},${facts.place.lng}` : ''
  useEffect(() => { setPlans({}) }, [placeKey])

  const rememberPlan = useCallback((personId: string, plan: DayPlan) => {
    setPlans((was) => ({ ...was, [`${personId}|${plan.date}`]: plan }))
  }, [])

  const chosenKey = state.appointment.salespersonId && state.appointment.date
    ? `${state.appointment.salespersonId}|${state.appointment.date}` : null
  const chosenPlan = chosenKey ? plans[chosenKey] ?? null : null

  /* The When box needs the chosen person's day even when no calendar fetched it (a phone's time button). */
  useEffect(() => {
    if (!open || !chosenKey || plans[chosenKey]) return
    const [person, date] = chosenKey.split('|') as [string, string]
    let live = true
    requestsApi.calendar({
      session: requestId, person, lat: facts.place?.lat ?? null, lng: facts.place?.lng ?? null,
      minutes: facts.minutes, orgUnitId: facts.orgUnitId, division: facts.divisionCode,
      segment: facts.segment, day: date,
    }).then((view) => { if (live && view.day.date === date) rememberPlan(person, view.day.plan) })
      .catch(() => {})
    return () => { live = false }
    /* The key and the place are the question; the facts it was asked with ride along. */
  }, [open, chosenKey, plans, requestId, placeKey])

  // -------------------------------------------------------------------------
  // Who goes, and when
  // -------------------------------------------------------------------------

  const setSchedule = useCallback((change: (was: ScheduleState | null) => ScheduleState | null) => {
    setScheduleState(change)
  }, [])

  const openCalendar = useCallback((person: PersonRef, options?: { choose?: boolean }) => {
    setScheduleState((was) => (was && was.mode === 'one' && was.people[0]?.id === person.id
      ? was : { mode: 'one', people: [person], day: null, first: null }))
    if (options?.choose) {
      setState((was) => ({ ...was, appointment: was.appointment.salespersonId === person.id
        ? was.appointment
        : { ...was.appointment, salespersonId: person.id, salespersonName: person.name,
          startsAt: null, date: null, start: null } }))
    }
  }, [])

  const compareCalendars = useCallback((people: PersonRef[]) => {
    setScheduleState({ mode: 'side', people: people.slice(0, 3), day: null, first: null })
  }, [])

  const closeSchedule = useCallback(() => setScheduleState(null), [])

  const choose = useCallback((person: PersonRef, time: ChosenTime | null) => {
    setBookAnyway(null)
    setState((was) => ({
      ...was,
      appointment: {
        ...was.appointment,
        salespersonId: person.id,
        salespersonName: person.name,
        startsAt: time?.startsAt ?? null,
        date: time?.date ?? null,
        start: time?.start ?? null,
      },
    }))
    if (time) setCurrent('appointment')
  }, [])

  const moveTo = useCallback((time: ChosenTime) => {
    setBookAnyway(null)
    setState((was) => ({ ...was, appointment: { ...was.appointment,
      startsAt: time.startsAt, date: time.date, start: time.start } }))
  }, [])

  // -------------------------------------------------------------------------
  // What Create is missing (§11.3)
  // -------------------------------------------------------------------------

  const salespersonFirst = firstWord(state.appointment.salespersonName)
  const missing: Missing[] | null = pressed
    ? missingFor(state, pressed.bookLater, salespersonFirst || null)
      .filter((one) => !book || one.section === 'appointment')
    : null
  const hintFor = useCallback((field: Missing['field']) =>
    missing?.find((one) => one.field === field)?.hint ?? null, [missing])

  /* Once everything that was missing is there, the red goes. */
  useEffect(() => { if (pressed && missing && missing.length === 0) setPressed(null) }, [pressed, missing])

  // -------------------------------------------------------------------------
  // Create (§11.2) and Book (§11.4)
  // -------------------------------------------------------------------------

  const toInput = (bookLater: boolean): NewRequestInput => {
    const now = stateRef.current
    const band = BUDGET_BANDS.find((one) => one.key === now.job.budgetKey)
    const channel = channelFor(now, setup?.smsReady ?? false)
    const contact: NewRequestInput['contact'] = now.contact.kind === 'chosen'
      ? { id: now.contact.contact.id }
      : (() => {
        const fields = now.contact.kind === 'new' ? now.contact.fields : EMPTY_TYPED_CONTACT
        return {
          firstName: fields.firstName.trim(), lastName: fields.lastName.trim(),
          company: fields.company.trim() || null, title: fields.title.trim() || null,
          phone: fields.phone.trim() ? { kind: fields.phoneKind, number: formatPhone(fields.phone) } : null,
          email: fields.email.trim() || null, prefers: fields.prefers,
          ...(fields.allowPhoneMatch ? { allowPhoneMatch: true as const } : {}),
        }
      })()
    /* Create makes what is there (Aaron, step 28): an untouched place or job is not sent. */
    let property: NewRequestInput['property'] = null
    if (now.property.kind === 'chosen') {
      property = { id: now.property.property.id,
        ...(now.contact.kind === 'new' ? { linkContact: true as const } : {}) }
    } else if (now.property.kind === 'new' && statusOf(now, 'property') !== 'empty') {
      const typed = now.property.property
      const address = typed.address
      property = {
        line1: address.line1.trim(), line2: address.line2.trim() || null, city: address.city.trim(),
        state: address.state, postalCode: address.postalCode.trim(),
        latitude: address.latitude!, longitude: address.longitude!, placeId: address.placeId,
        verified: address.verified, type: typed.type, name: typed.name.trim(),
        nameIsAuto: typed.nameIsAuto, zoneId: typed.zoneId,
        orgUnitId: (typed.orgUnitId ?? typed.check?.branch?.id)!,
      }
    }
    const withJob = statusOf(now, 'job') !== 'empty'
    const started = withJob && appointmentStarted(now)
    const timed = withJob && Boolean(now.appointment.startsAt && now.appointment.salespersonId)
    return {
      requestId,
      contact,
      property,
      job: !withJob ? null : {
        title: now.job.title.trim(),
        divisionCode: now.job.divisionCode!,
        leadSourceId: now.job.newLeadSource ? null : now.job.leadSourceId,
        newLeadSource: now.job.newLeadSource,
        wanted: wantedToSend(now.job),
        budgetLow: band?.low ?? null,
        budgetHigh: band?.high ?? null,
      },
      description: now.appointment.description.trim(),
      salespersonId: withJob ? now.appointment.salespersonId : null,
      appointment: bookLater
        ? (started ? { startsAt: null, minutes: facts.minutes } : null)
        : timed ? { startsAt: now.appointment.startsAt, minutes: facts.minutes } : null,
      confirm: !bookLater && timed && now.appointment.confirm && channel.channel
        ? confirmOf(now, channel.channel) : null,
      bookLater,
    }
  }

  const said = (result: NewRequestResult, bookLater: boolean): RequestOutcome['toast'] => {
    const now = stateRef.current
    const person = now.appointment.salespersonName
    const first = firstWord(person)
    if (bookLater) {
      if (!result.appointmentId) {
        return { title: `${result.reference} created`,
          sub: `${result.contactName} · ${result.propertyName} · no site visit yet`, openJob: false }
      }
      return { title: `${result.reference} created`,
        sub: person ? `Site visit with ${person} to book · it’s in ${first}’s To book too` : 'Site visit to book',
        openJob: false }
    }
    const when = now.appointment.date && now.appointment.start !== null
      ? `${dayShort(now.appointment.date)}, ${clock(now.appointment.start)} with ${person}` : ''
    const sent = result.confirmation
      ? ` · ${firstWord(result.contactName)} was ${result.confirmation.channel === 'sms' ? 'texted' : 'emailed'}`
      : ''
    /* The sheet lands on what it made (Aaron, step 28), so the toast never offers to open it again. */
    if (!result.jobId) {
      return result.propertyName
        ? { title: result.propertyName, sub: `${result.contactName} · no job yet`, openJob: false }
        : { title: result.contactName, sub: 'no property or job yet', openJob: false }
    }
    if (!result.appointmentId) {
      return { title: `${result.reference} created`,
        sub: `${result.contactName} · ${result.propertyName} · no site visit yet`, openJob: false }
    }
    return { title: `${result.reference} · ${result.title}`, sub: `${when}${sent}`, openJob: false }
  }

  const showMissing = (list: Missing[]) => {
    const sections = [...new Set(list.map((one) => one.section))]
    setOpenSections((was) => order([
      ...was.filter((one) => statusOf(stateRef.current, one) !== 'ready' || sections.includes(one)),
      ...sections,
    ]))
    const first = list[0]!
    setCurrent(first.section)
    scrollTo(first.section)
    if (first.field !== 'time' && first.field !== 'contact') requestFocus(first.field)
  }

  const create = async (bookLater: boolean) => {
    if (busy || !setup) return
    setProblem(null)
    if (book) { await doBook(); return }
    const list = missingFor(stateRef.current, bookLater, salespersonFirst || null)
    if (list.length > 0) {
      /* A phone has no second button, so Create without a time asks (Board 16 D). */
      if (phone && !bookLater && list.every((one) => one.field === 'time')) {
        setAskNoTime(true)
        return
      }
      setPressed({ bookLater })
      showMissing(list)
      return
    }
    setBusy(true)
    try {
      const result = await requestsApi.create(toInput(bookLater))
      onDone({ jobId: result.jobId, reference: result.reference, landing: result.landing,
        toast: said(result, bookLater) })
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'The request could not be created.')
    } finally {
      setBusy(false)
    }
  }

  const doBook = async () => {
    const now = stateRef.current
    if (!book) return
    if (!(now.appointment.startsAt && now.appointment.salespersonId)) {
      setPressed({ bookLater: false })
      showMissing(missingFor(now, false, salespersonFirst || null)
        .filter((one) => one.section === 'appointment'))
      return
    }
    const channel = channelFor(now, setup?.smsReady ?? false)
    const input: BookRequestInput = {
      requestId, jobId: book.jobId, appointmentId: book.appointmentId,
      salespersonId: now.appointment.salespersonId, startsAt: now.appointment.startsAt,
      minutes: facts.minutes, description: now.appointment.description.trim(),
      confirm: now.appointment.confirm && channel.channel ? confirmOf(now, channel.channel) : null,
    }
    setBusy(true)
    try {
      const result = await requestsApi.book(input)
      const person = now.appointment.salespersonName
      const when = now.appointment.date && now.appointment.start !== null
        ? `${dayShort(now.appointment.date)}, ${clock(now.appointment.start)} with ${person}` : ''
      const sent = result.confirmation
        ? ` · ${firstWord(contactName(now))} was ${result.confirmation.channel === 'sms' ? 'texted' : 'emailed'}`
        : ''
      onDone({ jobId: result.jobId, reference: result.reference, landing: null,
        /* Booked from the job's own Next up, the job is already open. */
        toast: { title: `${result.reference} · site visit booked`, sub: `${when}${sent}`,
          openJob: !window.location.pathname.startsWith(`/jobs/${result.jobId}`) } })
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'The site visit could not be booked.')
    } finally {
      setBusy(false)
    }
  }

  // -------------------------------------------------------------------------
  // The API the sections read
  // -------------------------------------------------------------------------

  const update = useCallback((change: (was: SheetState) => SheetState) => setState(change), [])

  const api: RequestSheetApi | null = setup ? {
    requestId, setup, phone, narrow, state, update, facts, book, current, openSection,
    chooseContact: (id, options) => { void chooseContact(id, options) },
    chooseProperty: (id, options) => { void chooseProperty(id, options) },
    replaceAsk: replaceAsk ? { section: replaceAsk.section, name: replaceAsk.name } : null,
    answerReplace: (yes) => {
      const ask = replaceAsk
      setReplaceAsk(null)
      if (yes && ask) ask.apply()
    },
    clearSection, focus, requestFocus, hintFor, missing,
    recommendation, recommendationLoading,
    schedule, setSchedule, openCalendar, compareCalendars, closeSchedule,
    choose, moveTo, chosenPlan, rememberPlan, bookAnyway, setBookAnyway,
  } : null

  // -------------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------------

  if (!open) return null

  const ready = readyCount(state)
  const note = book
    ? bookNote(state, { salespersonFirstName: salespersonFirst || null, reference: book.reference })
    : footerNote(state, { salespersonFirstName: salespersonFirst || null })
  const noteText: ReactNode = problem
    ? <span className="text-danger">{problem}</span>
    : missing && missing.length > 0
      ? <span className="text-danger"><b>{missingWords(missing)}</b>{missing.map((one) => one.short).join(' · ')}</span>
      : note.kind === 'keys'
        ? <span className="inline-flex items-center gap-1 text-text-muted"><Keycap>⌘</Keycap><Keycap>↵</Keycap> creates</span>
        : note.kind === 'needed'
          ? <span className="text-text-muted">{note.text}</span>
          : note.kind === 'creates'
            ? (
              <span className="text-text-muted">
                {note.parts.map((part, index) => (part.strong
                  ? <b key={index} className="font-bold text-text">{part.text}</b>
                  : <span key={index}>{part.text}</span>))}
              </span>
            )
            : <span className="text-danger">{note.text}</span>

  /* The board's note wraps onto a second line rather than being cut off (Board 6). */
  const noteNode = <span className="block whitespace-normal leading-[1.4]">{noteText}</span>

  const name = subjectName(state)
  const started = setup ? startedAt(setup.now, setup.timeZone) : ''
  const sub = book
    ? `${book.reference} · ${book.propertyName}`
    : `${name ? `${name} · ` : ''}started ${started}`

  const mark = (
    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-accent-line bg-accent-soft text-accent-dark">
      {book ? <IconCalendar size={18} /> : <PlusGlyph size={18} stroke={1.9} />}
    </span>
  )

  const asideShown = Boolean(schedule) && !phone && !narrow

  return (
    <>
      <Sheet
        open
        onClose={onClose}
        kind="job"
        mark={mark}
        title={book ? 'Book a site visit' : 'New request'}
        sub={sub}
        dirty={anythingTyped(state) && !book}
        saveLabel={book ? 'Book' : 'Create'}
        onSave={() => { void create(false) }}
        note={noteNode}
        roomyFooter
        secondary={book ? undefined : (
          <Button className={FOOTER_BUTTON} onClick={() => { void create(true) }}>Create, book later</Button>
        )}
        primary={(
          <Button variant="primary" className={FOOTER_BUTTON} onClick={() => { void create(false) }}>
            {!book && <CheckGlyph size={15} />}
            {book ? 'Book' : 'Create request'}
          </Button>
        )}
        aside={asideShown && api ? <RequestSheetProvider value={api}><ScheduleAside /></RequestSheetProvider> : undefined}
      >
        {api
          ? (
            <RequestSheetProvider value={api}>
              <SheetBody
                initialSearch={prefill?.search}
                bodyRef={body}
                searchInput={searchInput}
                phone={phone}
                statuses={statuses}
                ready={ready}
                openSections={openSections}
                asideOpen={Boolean(schedule)}
                enter={enter}
                onSearchChoice={onSearchChoice}
              />
            </RequestSheetProvider>
          )
          : <div className="py-10 text-center text-[13px] text-text-muted">{problem ?? 'Loading…'}</div>}
      </Sheet>

      {api && schedule && narrow && (
        <RequestSheetProvider value={api}>
          <div className="fixed inset-y-0 right-0 z-[55] flex w-[620px] max-w-full flex-col border-l border-line bg-surface-alt">
            <NarrowSchedule />
          </div>
        </RequestSheetProvider>
      )}

      {api && schedule && phone && (
        <RequestSheetProvider value={api}>
          <div className="fixed inset-0 z-[60] flex flex-col bg-bg">
            <PhoneSchedule />
          </div>
        </RequestSheetProvider>
      )}

      <BottomMenu open={askNoTime} onClose={() => setAskNoTime(false)} label="Create without a time?">
        <div className="px-[18px] pb-2.5 pt-1">
          <b className="text-[16px] font-bold text-text">Create without a time?</b>
          <div className="mt-[3px] text-[12.5px] text-text-muted">
            {state.appointment.salespersonName
              ? `${state.appointment.salespersonName} is chosen — no time is picked yet.`
              : 'Nobody is chosen yet.'}
          </div>
        </div>
        <SheetRow
          tall
          icon={<IconCalendar size={17} />}
          tone="accent"
          title="Pick a time"
          sub={state.appointment.salespersonId
            ? `opens ${salespersonFirst}’s two weeks` : 'search anyone who sells'}
          onClick={() => {
            setAskNoTime(false)
            if (state.appointment.salespersonId && state.appointment.salespersonName) {
              openCalendar({ id: state.appointment.salespersonId, name: state.appointment.salespersonName,
                firstName: salespersonFirst })
            } else {
              openSection('appointment')
            }
          }}
        />
        <SheetRow
          tall
          icon={<CheckGlyph size={16} stroke={2} />}
          tone="plain"
          title="Create, book later"
          sub={appointmentStarted(state)
            ? (salespersonFirst ? `the site visit goes to ${salespersonFirst}’s To book` : 'the site visit is saved to book later')
            : 'the job opens'}
          onClick={() => { setAskNoTime(false); void create(true) }}
        />
      </BottomMenu>
    </>
  )
}

// ---------------------------------------------------------------------------
// The body: the search and the bar, then the four sections in order
// ---------------------------------------------------------------------------

function SheetBody({
  bodyRef, searchInput, phone, statuses, ready, openSections, asideOpen, enter, onSearchChoice, initialSearch,
}: {
  /** An unknown number from the Inbox, typed in for the person (Activity, Board 8 B). */
  initialSearch?: string
  bodyRef: React.RefObject<HTMLDivElement>
  searchInput: React.RefObject<HTMLInputElement>
  phone: boolean
  statuses: Record<SectionKey, SectionStatus>
  ready: number
  openSections: SectionKey[]
  asideOpen: boolean
  enter: (section: SectionKey, options?: { scroll?: boolean }) => void
  onSearchChoice: (choice: SearchChoice) => void
}) {
  /* The body sits inside the provider, so it reads the same API the sections do. */
  const api = useRequestSheet()
  const { state, setup, book } = api

  /*
   * Focus in the search when the sheet opens (§5.1) — unless somebody has
   * already clicked into a section in those 60 ms, whose caret it would steal.
   */
  useEffect(() => {
    if (book) return
    const timer = window.setTimeout(() => {
      if (document.activeElement?.closest('[data-request-section]')) return
      searchInput.current?.focus()
    }, 60)
    return () => window.clearTimeout(timer)
  }, [book, searchInput])

  /*
   * The calendars fold the ready sections to one line each (Board 7), so the
   * form gets much shorter; it starts again from the top, where those lines are.
   */
  useEffect(() => {
    if (!asideOpen) return
    const scroller = bodyRef.current?.closest('.overflow-y-auto') as HTMLElement | null
    scroller?.scrollTo?.({ top: 0 })
  }, [asideOpen, bodyRef])

  type Look = 'open' | 'card' | 'line' | 'amber' | 'waiting'
  const scheduling = asideOpen || Boolean(api.schedule) || state.appointment.salespersonId !== null
  const lookOf = (section: SectionKey): Look => {
    const status = statuses[section]
    if (book && section !== 'appointment') return 'line'
    /*
     * A section somebody opened is open, calendars or not (Aaron, step 28:
     * "once the site visit has been set, I can't reopen the contact" — Open
     * did nothing while the calendar was up). The calendars still fold the
     * ready ones when they open; see the effect on `schedule`.
     */
    if (openSections.includes(section)) return 'open'
    /* While the calendars are open, ready sections fold to one line (§5.0, Board 7) — not the one being booked. */
    if (asideOpen && status === 'ready' && section !== 'appointment') return 'line'
    /*
     * Ready, the contact, the place and the job are each a headed card with
     * Edit (Aaron, step 28: "the job rollup should look the same as the
     * contact and property rollup"), and all three fold to one line together
     * once scheduling starts — a calendar open or somebody chosen to go
     * (Boards 7, 9 A, 13, 16 B).
     */
    if (status === 'ready') return scheduling || section === 'appointment' ? 'line' : 'card'
    if (status === 'started') return 'amber'
    return 'waiting'
  }

  const nextWaiting = SECTIONS.find((one) => lookOf(one) === 'waiting'
    && SECTIONS.slice(0, SECTIONS.indexOf(one)).every((before) => statuses[before] !== 'empty'
      || openSections.includes(before)))

  const lineFor = (section: SectionKey): { title: string; summary: string } => {
    const status = statuses[section]
    if (status !== 'ready') {
      const summary = section === 'contact' ? contactSummary(state)
        : section === 'property' ? propertySummary(state)
          : section === 'job' ? jobSummaryOf(state, setup) : appointmentSummaryOf(state, setup)
      return { title: SECTION_TITLES[section], summary }
    }
    switch (section) {
      case 'contact': {
        const isNew = state.contact.kind === 'new'
        /* A phone's row has room for the one fact a caller needs: *new · prefers Text* (Board 16 B). */
        if (phone) {
          const prefers = state.contact.kind === 'chosen' ? state.contact.contact.prefers
            : state.contact.kind === 'new' ? state.contact.fields.prefers : null
          return { title: contactName(state) ?? '',
            summary: [isNew ? 'new' : null, prefers ? `prefers ${PREFERS_LABELS[prefers]}` : null]
              .filter(Boolean).join(' · ') }
        }
        const line = state.contact.kind === 'chosen'
          ? knownContactLine(state.contact.contact, false)
          : state.contact.kind === 'new'
            ? knownContactLine({ phone: state.contact.fields.phone.trim()
              ? formatPhone(state.contact.fields.phone) : null, phoneKind: null,
            prefers: state.contact.fields.prefers }, false) : ''
        return { title: contactName(state) ?? '', summary: [isNew ? 'new' : null, line].filter(Boolean).join(' · ') }
      }
      case 'property': {
        /* *new · 412 Orchard Ridge Dr, Mechanicsburg · West Shore* (Board 7). */
        const where = state.property.kind === 'chosen'
          ? [`${state.property.property.line1}, ${state.property.property.city}`,
            state.property.property.zoneName]
          : state.property.kind === 'new'
            ? [[state.property.property.address.line1, state.property.property.address.city]
              .filter(Boolean).join(', '),
            state.property.property.check?.zones.find((one) =>
              one.id === (state.property.kind === 'new' ? state.property.property.zoneId : null))?.name]
            : []
        /* A phone keeps the zone and drops the street it is named after: *new · West Shore* (16 B). */
        const said = phone && where.length === 2 && where[1] ? [where[1]] : where
        return { title: sheetPropertyName(state) ?? '',
          summary: [state.property.kind === 'new' ? 'new' : null, ...said].filter(Boolean).join(' · ') }
      }
      case 'job':
        return { title: state.job.title.trim(), summary: jobSummaryOf(state, setup) }
      default:
        return { title: 'Site visit', summary: appointmentSummaryOf(state, setup) }
    }
  }

  const sections: Record<SectionKey, (look: 'open' | 'card') => ReactNode> = {
    contact: (look) => <ContactSection mode={look} />,
    property: (look) => <PropertySection mode={look} />,
    job: (look) => <JobSection mode={look} />,
    appointment: () => <AppointmentSection />,
  }

  /*
   * Folding the sections above moves everything under the pointer. A press
   * that folded on its way down would land its click somewhere else — a
   * card's time button jumped up by the height of the Job fields and the
   * click hit nothing — so a pointer folds only once its click has gone
   * through, and the keyboard folds on focus as before.
   */
  const pressing = useRef<SectionKey | null>(null)
  useEffect(() => {
    const release = () => {
      const section = pressing.current
      if (!section) return
      window.setTimeout(() => {
        if (pressing.current === section) enter(section)
        pressing.current = null
      }, 0)
    }
    window.addEventListener('pointerup', release, true)
    window.addEventListener('pointercancel', release, true)
    return () => {
      window.removeEventListener('pointerup', release, true)
      window.removeEventListener('pointercancel', release, true)
    }
  }, [enter])

  let previous: Look | null = null
  const items = SECTIONS.map((section) => {
    const look = lookOf(section)
    /*
     * The boards' spacing: 20px and a rule between open sections and cards
     * (Board 5), and the same under the one-line rows on a desktop or an iPad
     * (Boards 7 and 13); no rule and 10px under them on a phone (Board 16 B);
     * 12px before a waiting row (Board 2).
     */
    const gap = previous === null ? ''
      : look === 'open' || look === 'card'
        ? (previous === 'line' || previous === 'amber') && phone ? 'mt-2.5'
          : cn(phone ? 'mt-4 pt-3.5' : previous === 'waiting' ? 'mt-4 pt-4' : 'mt-5 pt-4',
            'border-t border-line-faint')
        : look === 'waiting'
          ? 'mt-3'
          : previous === 'line' || previous === 'amber' ? 'mt-1.5' : 'mt-3'
    previous = look
    const node = look === 'open' || look === 'card'
      ? sections[section](look)
      : look === 'waiting'
        ? <WaitingRow section={section} next={section === nextWaiting} onOpen={() => enter(section, { scroll: true })} />
        : (() => {
          const said = lineFor(section)
          return (
            <FoldedLine section={section} status={statuses[section]} title={said.title}
              summary={said.summary} onOpen={book ? undefined : () => enter(section, { scroll: true })} />
          )
        })()
    return (
      <div
        key={section}
        data-request-section={section}
        className={gap}
        onFocusCapture={() => {
          if ((look === 'open' || look === 'card') && pressing.current === null) enter(section)
        }}
        onPointerDownCapture={() => { if (look === 'open' || look === 'card') pressing.current = section }}
      >
        {node}
      </div>
    )
  })

  return (
    /*
     * `shrink-0`: at least the screen tall, never squeezed to it — the grey
     * runs to the end (Board 15), over the scroller's own 24px of bottom
     * padding too, or a short form ends in a white band (14 A).
     */
    <div ref={bodyRef} className={cn('flex flex-col', phone && '-mx-4 -mb-6 min-h-[calc(100%+1.5rem)] shrink-0 bg-bg')}>
      {!book && (
        <div
          data-request-sticky
          /*
           * Sticky against the scroller's edge, not its padding: `top` is
           * minus the body's own top padding, or the fields scroll through
           * the gap above the search.
           */
          className={cn('sticky z-10 bg-surface',
            phone ? '-top-4 -mt-4 flex flex-col gap-2 border-b border-line-faint px-3.5 py-2.5'
              : '-top-[18px] -mx-5 -mt-[18px]')}
        >
          <div className={cn(!phone && 'px-5 pb-2.5 pt-3')}>
            <SearchBox phone={phone} canAddContacts={setup.canAddContacts} onChoose={onSearchChoice}
              inputRef={searchInput} initialText={initialSearch} />
          </div>
          <div className={cn(!phone && 'border-b border-line-faint px-5 py-2.5')}>
            <SectionBar statuses={statuses} current={api.current} ready={ready} phone={phone}
              onChoose={(section) => enter(section, { scroll: true })} />
          </div>
        </div>
      )}
      <div className={cn('flex flex-col', phone ? 'px-4 pb-6 pt-3.5' : book ? '' : 'pt-[18px]')}>
        {items}
      </div>
    </div>
  )
}

const SECTION_TITLES: Record<SectionKey, string> = {
  contact: 'Contact', property: 'Property', job: 'Job', appointment: 'Appointment',
}

